import React, { useCallback } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import queryString from 'query-string';
import { compose } from 'redux';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router';

// GLOBAL VARIABLES
import { GLOBAL_LOCAL, NEW_USED } from 'codes.js';
import { RECORD } from 'pathnames.js';
import { ENDPOINTS } from 'endpoints.js';

// GLOBAL FUNCTIONS
import { apiFetch, makePath } from 'functions';

// HELPERS
import { documentTitle } from 'helpers/documentTitle';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';
import Dropdown from 'core/tools/dropdown/Dropdown';
import Item from 'routers/interface/tools/Item';

// STYLES
import styles from './title.module.scss';

// MAIN COMPONENT
const Title = compose(
  documentTitle,
)(({
  children,
  title,
  tools,
  rowClassName = '',
  className,
  toolsClassName,
  onClick
}) => !title ? null : (
  <div className={clsx(styles.container, className)} onClick={onClick}>
    <h2 className={styles.title}>{title || children}</h2>
    {!!tools &&
      <div className={clsx(styles.tools, toolsClassName)}>
        <div className={clsx(styles.row, rowClassName)}>
          {tools}
        </div>
      </div>
    }
  </div>
))

// CHILD COMPONENT
const AddRecord = ({ label = 'record', pathname, params }) => {

  // REACT ROUTER
  const location = useLocation();

  // RENDER
  return (
    <Button.Add
      label={`Add ${label}`}
      to={`${makePath(pathname || location.pathname, RECORD.add)}?${queryString.stringify(params)}`}
    />
  )
}
const AddNewUsed = ({ label = '', pathname, params = {} }) => (<>
  <AddRecord
    label={`New ${label}`}
    pathname={pathname}
    params={{
      new_used: NEW_USED.new.numeric,
      ...params
    }}
  />
  <AddRecord
    label={`Pre-Owned ${label}`}
    pathname={pathname}
    params={{
      new_used: NEW_USED.used.numeric,
      ...params
    }}
  />
</>)

const AddProducts = ({ label = '', pathname, productType }) => {
  const navigate = useNavigate();

  const options = [
    {
      label: 'New',
      value: [1],
    },
    {
      label: 'Pre-Owned',
      value: [0],
    },
    {
      label: 'Both',
      value: [0, 1]
    }
  ];

  const handleOnSelect = useCallback((option) => {
    const params = new URLSearchParams();
    params.append('product_type', productType);
    params.append('new_used', option);
    navigate(`${makePath(pathname || window.location.pathname, RECORD.add)}?${params.toString()}`);
  }, [pathname, productType, navigate]);

  return (
    <Dropdown
      toggle={{
        label,
        size: 'sm',
        outline: true
      }}
    >
      {options.map(({ label, value }) => (
        <Item option={label} key={value} onSelect={() => handleOnSelect(value)} />
      ))}
    </Dropdown>
  )
}

const AddGlobalLocal = ({ label = '', pathname, params = {} }) => (<>
  <AddRecord
    label={`Global ${label}`}
    pathname={pathname}
    params={{
      global_local: GLOBAL_LOCAL.global.numeric,
      ...params
    }}
  />
  <AddRecord
    label={`Local ${label}`}
    pathname={pathname}
    params={{
      global_local: GLOBAL_LOCAL.local.numeric,
      ...params
    }}
  />
</>)

const RefreshKpiReports = () => {

  // CALLBACKS
  const handleRefresh = useCallback(
    () => {
      apiFetch({
        method: 'PUT',
        endpoint: ENDPOINTS.dashboard.report.refreshKpi,
        loadingMessage: 'Refreshing KPI reports',
        successMessage: 'Refreshed KPI reports.',
        errorMessage: 'Unable to refresh KPI reports.',
      })
    },
    []
  )

  // RENDER
  return (
    <Button
      label="Refresh KPI Reports"
      outline
      icon="refresh"
      disabled={false}
      onClick={handleRefresh}
    />
  )
}

// EXPORT
Title.AddRecord = AddRecord;
Title.AddNewUsed = AddNewUsed;
Title.AddGlobalLocal = AddGlobalLocal;
Title.AddProducts = AddProducts;
Title.RefreshKpiReports = RefreshKpiReports;

export default Title;
